import { Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ReactNode } from 'react';

import { bubbleRadius, slideIn } from '../assistant-theme';

interface AbstractMessageBubbleProps {
  /** Which side of the conversation the bubble sits on. */
  side: 'start' | 'end';
  /** Shown before the bubble, on the side the message comes from. */
  avatar?: ReactNode;
  /** What sets a variant apart: its colours and how its content is laid out. */
  bubbleSx?: SxProps<Theme>;
  children: ReactNode;
}

/** The frame every message bubble shares: where it sits, how it enters and its shape. */
export default function AbstractMessageBubble({
  side,
  avatar,
  bubbleSx = {},
  children,
}: AbstractMessageBubbleProps) {
  const isEnd = side === 'end';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        justifyContent: isEnd ? 'flex-end' : 'flex-start',
        animation: `${slideIn} 0.25s ease-out`,
      }}
    >
      {avatar}
      <Typography
        component="div"
        variant="body2"
        sx={[
          (theme: Theme) => ({
            maxWidth: '80%',
            px: 1.75,
            py: 1,
            borderRadius: bubbleRadius(theme, isEnd),
            overflowWrap: 'anywhere',
            // Tables scroll sideways inside the bubble instead of widening it.
            '& table': {
              display: 'block',
              maxWidth: '100%',
              overflowX: 'auto',
              borderCollapse: 'collapse',
              mt: 1,
            },
            '& th, & td': {
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
              borderBottom: 1,
              borderColor: 'divider',
            },
            '& th': { fontWeight: 600 },
          }),
          ...(Array.isArray(bubbleSx) ? bubbleSx : [bubbleSx]),
        ]}
      >
        {children}
      </Typography>
    </Box>
  );
}
